"use client"; // Error components must be Client Components

import { type ReactNode } from "react";
import { z } from "zod";
import DOMPurify from "isomorphic-dompurify";
import { useRouter } from "next/navigation";
import { BadContractState, BadRequestState, ContractValidityExpired } from "@/constants/errors";
import { useTranslation } from "@/i18n/client";
import Text from "@/components/Text";
import { Trans } from "react-i18next";
import Link from "@/components/Link";
import ContactLink from "@/components/ContactLink";
import { Box, Modal as MantineModal } from "@mantine/core";
import { Modal } from "@/components/Modal";
import ButtonsGroup from "@/components/ButtonsGroup";
import modalClasses from "@/components/Modal/index.module.css";

const errorWithTextFromBeSchema = z.object({
  response: z.object({
    data: z.object({
      error_message: z.object({
        title: z.string(),
        content: z.string(),
      }),
    }),
  }),
});

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const { t: tErrors } = useTranslation("errors");
  const { t } = useTranslation("common");

  const router = useRouter();

  // TODO Log the error to an error reporting service
  console.error(error);

  let title: ReactNode = t("somethingWentWrong");
  let description: ReactNode = t("somethingWentWrongDescription");
  let closeLabel = t("tryAgain");

  if (error.message === BadContractState) {
    title = tErrors("badContractState.title");
    description = tErrors("badContractState.description");
    closeLabel = tErrors("badContractState.closeLabel");
  }

  //currently is not in use, to be implemented when it is requested
  if (error.message === BadRequestState) {
    title = tErrors("badRequestState.title");
    description = description = (
      <Text variant="reading_medium" mb="1.5rem">
        <Trans
          t={tErrors}
          i18nKey="badRequestState.description"
          components={{
            eonLink: <Link href="http://www.prepis.cz/" key="prepisLink" />,
          }}
        />
      </Text>
    );
    closeLabel = tErrors("badRequestState.closeLabel");
  }

  if (error.message === ContractValidityExpired) {
    title = t("contractValidityExpired.title");
    description = (
      <Text variant="reading_medium" mb="1.5rem">
        <ContactLink href="mailto:info@eon.cz" i18nKey="contractValidityExpired.description" />
      </Text>
    );
    closeLabel = t("contractValidityExpired.closeLabel");
  }

  const errorParsed = errorWithTextFromBeSchema.safeParse(error);
  if (errorParsed.success) {
    title = DOMPurify.sanitize(errorParsed.data.response.data.error_message.title);
    description = DOMPurify.sanitize(errorParsed.data.response.data.error_message.content);
    closeLabel = tErrors("closeLabel");
  }

  return (
    <MantineModal opened centered size="md" withCloseButton={false} onClose={() => {}} classNames={modalClasses}>
      <Box pos="relative">
        <Modal.Title>{title}</Modal.Title>
        <Modal.Description description={description} />
        <ButtonsGroup
          rightButtonText={closeLabel}
          onRightButtonClick={() => router.push("https://www.eon.cz/")}
          withLeftButton={false}
        />
      </Box>
    </MantineModal>
  );
}
